/**
 * src/components/layout/CopilotPanel.tsx
 * ---------------------------------------
 * Ask Copilot — a side panel that answers questions about the console's current
 * state. The top-bar button used to be a label with no handler; this is what it
 * opens.
 *
 * Answers come from POST /copilot/ask, which grounds the org's configured
 * `agent` model in a live snapshot (active conversations, 24h sentiment split,
 * top intents, unresolved alerts), so replies reflect real data rather than the
 * model's general knowledge.
 */
import { useEffect, useRef, useState } from 'react'
import { askCopilot, type CopilotMessage } from '@/api/copilot'
import { Icon } from '@/components/ui/Icon'

const STARTERS = [
  'What needs my attention right now?',
  'How is sentiment trending today?',
  'What are customers asking about most?',
]

interface CopilotPanelProps {
  open: boolean
  onClose: () => void
}

export function CopilotPanel({ open, onClose }: CopilotPanelProps) {
  const [messages, setMessages] = useState<CopilotMessage[]>([])
  const [draft, setDraft] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const threadRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  useEffect(() => {
    const el = threadRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, pending])

  async function send(question: string) {
    const text = question.trim()
    if (!text || pending) return

    // Snapshot the history *before* appending, so the turn isn't sent twice.
    const history = messages
    setMessages([...history, { role: 'user', content: text }])
    setDraft('')
    setError(null)
    setPending(true)
    try {
      const { answer } = await askCopilot(text, history)
      setMessages((prev) => [...prev, { role: 'assistant', content: answer }])
    } catch {
      setError('Copilot couldn’t answer that. Check the model under LLM Config and try again.')
    } finally {
      setPending(false)
    }
  }

  if (!open) return null

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden
        style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,26,53,.28)' }}
      />
      <aside
        role="dialog"
        aria-label="Ask Copilot"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 71,
          width: 'min(430px, 100vw)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-elev)',
          borderLeft: '1px solid var(--line)',
          boxShadow: 'var(--sh-4)',
        }}
      >
        <header
          style={{
            padding: '15px 18px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Icon name="sparkle" size={16} style={{ color: 'var(--brand-blue)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ display: 'block', fontSize: 13.5, color: 'var(--ink)' }}>
              Ask Copilot
            </strong>
            <small style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              Answers from your live console data
            </small>
          </div>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close Copilot">
            <Icon name="close" size={14} />
          </button>
        </header>

        <div ref={threadRef} style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {messages.length === 0 && (
            <div>
              <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                Ask about what’s happening across your customer-service AI. Copilot reads
                the same live data as the Live Wall, Alerts and Dashboard.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {STARTERS.map((starter) => (
                  <button
                    key={starter}
                    onClick={() => void send(starter)}
                    style={{
                      padding: '10px 13px',
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--r-sm)',
                      background: 'var(--bg-sunken)',
                      color: 'var(--ink-2)',
                      fontSize: 12.5,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    {starter}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              style={{
                marginBottom: 14,
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={{
                  maxWidth: '88%',
                  padding: '10px 13px',
                  borderRadius: 'var(--r-md)',
                  fontSize: 12.5,
                  lineHeight: 1.62,
                  whiteSpace: 'pre-wrap',
                  ...(message.role === 'user'
                    ? { background: 'var(--brand-blue)', color: 'white' }
                    : {
                        background: 'var(--bg-sunken)',
                        color: 'var(--ink)',
                        border: '1px solid var(--line)',
                      }),
                }}
              >
                {message.content}
              </div>
            </div>
          ))}

          {pending && (
            <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: 0 }}>Thinking…</p>
          )}

          {error && (
            <p style={{ fontSize: 12, color: 'var(--err)', margin: '8px 0 0' }}>{error}</p>
          )}
        </div>

        <footer style={{ padding: 14, borderTop: '1px solid var(--line)' }}>
          <textarea
            ref={inputRef}
            className="textarea"
            value={draft}
            rows={2}
            placeholder="Ask about conversations, sentiment, alerts…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void send(draft)
              }
            }}
            style={{ width: '100%', resize: 'none' }}
          />
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <small style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>
              Read-only — Copilot never changes settings
            </small>
            <button
              className="btn brand sm"
              onClick={() => void send(draft)}
              disabled={pending || draft.trim().length === 0}
            >
              <Icon name="send" size={13} /> Ask
            </button>
          </div>
        </footer>
      </aside>
    </>
  )
}
